/** @format */
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Pagination,
    PaginationItem,
    TextField,
    Typography,
} from "@mui/material"
import { useRouter } from "next/router"
import React from "react"
import Slider from "react-slick"
import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"

import { getPageContents } from "../../components/contentful/pageProps"
import {
    Card,
    getHeroBanner,
    isContentActive,
} from "../../components/contentful"
import BreadCrumb from "../../components/BreadCrumb"
import Layout from "../../components/Layout"
import { NextLinkComposed } from "../../components/Link"
import Seo from "../../components/SEO"
import { containerCSS, MOCK_ERRORS } from "../../util"
import { getArticles } from "../../util/contentful/api"
import { getEnvironmentVariable } from "../../util/env"
import { handleError } from "../../util/handlers"
import FourOhFour from "../404"

const inputStyles = {
    "& .MuiOutlinedInput-root": {
        height: "auto",
        lineHeight: undefined,
        m: 0,
    },
}

let hasErrored = false

const Blog = ({
    articles,
    categories,
    content,
    preview,
    breadcrumbs,
    page,
    ...props
}) => {
    /**
     * Throw mock client-side errors once on the blog index page to test
     * `<AppErrorBoundary>`'s handling of errors outside React
     *
     * The `MOCK_ERRORS` environment variable acts as a feature flag to enable
     * this and must be set to "true"
     */
    const shouldThrowMockError =
        typeof window !== "undefined" && !hasErrored && MOCK_ERRORS === "true"

    // TODO: remove this at some point after the DVM-1642 PR is merged
    if (shouldThrowMockError) {
        hasErrored = true
        // Wrapped in a `setTimeout` to ensure the error isn't caught by
        // `componentDidCatch()` in the error boundary; this is to test handling of
        // errors thrown outside of React rendering
        setTimeout(() => {
            throw new Error("Test Error outside React")
        }, 1000)

        // To mock an unhandled promise rejection outside of React
        setTimeout(() => {
            /* eslint-disable-next-line promise/catch-or-return */
            Promise.reject(new Error("Test Error outside React in Promise"))
        }, 2000)
    }

    let { hero, pageHero, seo = {} } = content.fields || content
    seo = seo.fields || seo
    pageHero = hero || pageHero
    pageHero = pageHero ? [pageHero] : []

    const itemsPerPage = 5
    const isPageActive = content && isContentActive({ ...content, preview })
    const router = useRouter()
    const siteUrl = getEnvironmentVariable({ key: "SITE_URL" })

    const categoryFilter = router?.query?.category?.split("&") ?? []
    const numPages = Math.ceil(articles?.length / itemsPerPage)
    const renderedBlogs = articles?.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    )

    if (!isPageActive) return <FourOhFour />

    const handleChangePage = () => window.scroll({ top: 0, behavior: "smooth" })

    const hideBreadcrumbs = breadcrumbs && !breadcrumbs.length
    const hasCategories = categories?.length > 0
    // Used tp populate shareImage in SEO component
    const hasHero = pageHero.length > 0
    const firstHeroImage = pageHero[0]?.fields?.media?.fields?.file?.url

    const pageContents = (
        <>
            <Seo
                canonicalUrl={`${siteUrl}${router.asPath}`}
                title={seo.title}
                description={seo.description}
                noIndex={seo.noindex}
                noFollow={seo.nofollow}
                shareImage={
                    hasHero && firstHeroImage
                        ? `https:${firstHeroImage}`
                        : undefined
                }
            />

            <Grid alignItems="center" container flexDirection="column">
                {hasHero && (
                    <Grid container item>
                        <Grid
                            item
                            xs={12}
                            css={css`
                                & .slick-prev,
                                .slick-next {
                                    z-index: 99;
                                }
                                & .slick-prev {
                                    left: 8px !important;
                                }
                                & .slick-next {
                                    right: 8px !important;
                                }
                            `}
                        >
                            <Slider
                                {...{
                                    dots: true,
                                    infinite: true,
                                    speed: 500,
                                    slidesToShow: 1,
                                    slidesToScroll: 1,
                                }}
                            >
                                {getHeroBanner(pageHero)}
                            </Slider>
                        </Grid>
                    </Grid>
                )}

                {/* unless passed in props, navigation will be based on the URL paths */}
                {!hideBreadcrumbs && <BreadCrumb navigations={breadcrumbs} />}

                <Grid
                    container
                    item
                    sx={{
                        containerCSS,
                        display: {
                            sm: "flex",
                            md: hasCategories ? "grid" : "flex",
                        },
                        gridTemplateColumns: {
                            sm: undefined,
                            md: hasCategories ? "242px 1fr" : undefined,
                        },
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    {hasCategories && (
                        <Grid
                            item
                            sx={{
                                display: { xs: "none", md: "block" },
                            }}
                        >
                            <Typography>Blog Categories</Typography>
                            <List dense>
                                {categories.map((category, index) => (
                                    <ListItem
                                        disablePadding
                                        key={`side-list-${category}-${index}`}
                                    >
                                        <ListItemButton
                                            LinkComponent={NextLinkComposed}
                                            to={{
                                                pathname: "/blog",
                                                query: {
                                                    category:
                                                        category.toLowerCase(),
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={category}
                                                primaryTypographyProps={{
                                                    textTransform: "capitalize",
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    )}
                    <Grid
                        container
                        item
                        justifyContent="center"
                        sx={{
                            p: { xs: "1.5rem", md: 0 },
                            pl: { xs: undefined, md: 2 },
                            maxWidth: "40rem",
                            mt: 0,
                            mb: 2,
                            width: "100%",
                        }}
                        rowSpacing={2}
                    >
                        {hasCategories && (
                            <Grid item sx={{ width: "100%" }}>
                                <Autocomplete
                                    multiple
                                    fullWidth
                                    limitTags={2}
                                    id="blog-category-filter"
                                    options={categories}
                                    disableCloseOnSelect
                                    renderOption={(
                                        props,
                                        option,
                                        { selected }
                                    ) => (
                                        <Box
                                            component="li"
                                            {...props}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            <Checkbox checked={selected} />
                                            {option}
                                        </Box>
                                    )}
                                    sx={inputStyles}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Filter by Category"
                                            placeholder="Category"
                                            sx={inputStyles}
                                        />
                                    )}
                                    value={categoryFilter}
                                    onChange={(event, newValue) => {
                                        if (newValue?.length) {
                                            router.push({
                                                href: router.asPath,
                                                query: {
                                                    category:
                                                        newValue.join("&"),
                                                },
                                            })
                                        } else {
                                            router.push({ href: router.asPath })
                                        }
                                    }}
                                />
                            </Grid>
                        )}
                        <Grid
                            container
                            item
                            justifyContent="center"
                            rowSpacing={4}
                        >
                            {renderedBlogs?.map(
                                (
                                    {
                                        category,
                                        heading,
                                        image,
                                        name,
                                        path,
                                        summary,
                                    },
                                    index
                                ) => {
                                    const button = {
                                        fields: {
                                            name: `Read More - ${heading}`,
                                            path,
                                            text: "Read More",
                                            variant: "outlined",
                                            fullWidth: false,
                                            disabled: false,
                                            isHidden: false,
                                        },
                                        __typename: "CtaButton",
                                    }
                                    return (
                                        <Grid key={`blog-${index}`} item>
                                            <Card
                                                name={name}
                                                body={summary}
                                                image={image}
                                                title={heading}
                                                actionAlignment="bottom"
                                                contentAlignment="flex-start"
                                                hideImage={false}
                                                hideOnMobile={false}
                                                hideOnDesktop={false}
                                                imageAlignment="stacked"
                                                imageAlignmentMobile="stacked"
                                                variant="outlined"
                                                actions={[button]}
                                            >
                                                {category &&
                                                    category?.length > 0 && (
                                                        <Grid
                                                            container
                                                            item
                                                            alignContent="end"
                                                            columnSpacing={2}
                                                            sx={{
                                                                pt: 2,
                                                                width: "100%",
                                                            }}
                                                        >
                                                            {category.map(
                                                                (
                                                                    item,
                                                                    index
                                                                ) => (
                                                                    <Grid
                                                                        item
                                                                        key={`${item}-${index}`}
                                                                    >
                                                                        <Chip
                                                                            clickable
                                                                            component={
                                                                                NextLinkComposed
                                                                            }
                                                                            label={
                                                                                item
                                                                            }
                                                                            to={{
                                                                                pathname:
                                                                                    "/blog",
                                                                                query: {
                                                                                    category:
                                                                                        item.toLowerCase(),
                                                                                },
                                                                            }}
                                                                            variant="outlined"
                                                                        />
                                                                    </Grid>
                                                                )
                                                            )}
                                                        </Grid>
                                                    )}
                                            </Card>
                                        </Grid>
                                    )
                                }
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    <Pagination
                        count={numPages}
                        page={Number(page)}
                        onChange={handleChangePage}
                        variant="outlined"
                        shape="rounded"
                        color="primary"
                        renderItem={(item) => {
                            const categoryQuery = router.query?.category?.length
                                ? { category: router.query.category }
                                : {}
                            const query =
                                item.page === 1
                                    ? categoryQuery
                                    : { ...router.query, page: item.page }

                            return (
                                <PaginationItem
                                    component={NextLinkComposed}
                                    to={{
                                        pathname: "/blog",
                                        query: { ...query },
                                    }}
                                    {...item}
                                />
                            )
                        }}
                    />
                </Grid>
            </Grid>
        </>
    )

    return (
        <Layout preview={preview} {...props}>
            {pageContents}
        </Layout>
    )
}

export async function getServerSideProps({ ...props }) {
    const {
        preview,
        query: { category = "", page = 1 },
    } = props
    let categories = []
    let filteredArticles = []

    try {
        const articles =
            (await getArticles({
                order: "-fields.date", //returns articles in descending order based on their date field. Newest -> Oldest
                resolveLinks: true,
                preview,
            })) ?? []

        categories = [
            ...new Set(
                articles?.flatMap(
                    ({ category }) =>
                        category?.map((item) => item.toLowerCase()) ?? []
                )
            ),
        ].sort()

        const categoryList = category.split("&")
        filteredArticles = category
            ? articles?.filter(({ category }) =>
                  category?.some((item) =>
                      categoryList.includes(item.toLowerCase())
                  )
              )
            : articles
    } catch (err) {
        console.log("Blog page: Get articles error")
        handleError(err, false)
    }

    const itemsPerPage = 5
    const numPages = Math.ceil(filteredArticles?.length / itemsPerPage)
    if (numPages < page) return { notFound: true }

    return await getPageContents(
        {
            articles: filteredArticles,
            categories,
            page,
            ...props,
            breadcrumbs: null,
        },
        { name: "Blog", isIsr: false }
    )
}

export default Blog
